import Groups from './tables/Groups';
import Posts from './tables/Posts';
import PostsToTags from './tables/PostsToTags';
import ProfileMetrics from './tables/ProfileMetrics';
import Profiles from './tables/Profiles';
import ProfilesToGroups from './tables/ProfilesToGroups';
import Tags from './tables/Tags';
import Client from 'sprout-api-client/src/Client';
import ConnectionData from './ConnectionData';
import PostPerformance from './standard-connections/PostPerformance';
import ProfilePerformance from './standard-connections/ProfilePerformance';
import TagPerformance from './standard-connections/TagPerformance';
import { dateFromOptionKey } from "../DateOptions";
var BASE_API_URL = ''; // this will be served on the same domain, so no base url to avoid cors issues
var USE_CASE_HEADER = 'bi-connector-tableau';
var ANALYTICS_SUPPORTED_CUSTOMER_PROFILE_NETWORK_TYPES = [
    'facebook',
    'fb_instagram_account',
    'linkedin_company',
    'pinterest',
    'tiktok',
    'twitter',
    'youtube',
    'linkedin',
    'threads',
    'bluesky',
    'snapchat',
];
var Connector = /** @class */ (function () {
    function Connector(name, tableau, fetch) {
        this.tableau = tableau;
        this.fetch = fetch;
        this.tables = new Map();
        this.standardConnections = new Array();
        this.setUpSchema();
        var tableauConnector = this.tableau.makeConnector();
        tableauConnector.init = this.tableau_init.bind(this);
        tableauConnector.getSchema = this.tableau_getSchema.bind(this);
        tableauConnector.getData = this.tableau_getData.bind(this);
        // Use the stub takeover function if available (avoids a second registerConnector call
        // that would reset the WDC SDK's state and silently break the init handler).
        // Falls back to direct registration if the stub is not present.
        if (typeof window.__sproutConnectorReady === 'function') {
            window.__sproutConnectorReady(tableauConnector);
        }
        else {
            this.tableau.registerConnector(tableauConnector);
        }
        this.tableau.connectionName = name;
        this.connectorInitialized = false;
        this.startDate = new Date('2022-01-01T00:00:00Z');
        this.endDate = new Date();
    }
    Connector.prototype.registerConnectorInitCallback = function (callback) {
        this.connectorInitCallback = callback;
    };
    Connector.prototype.tableauSubmitEndOfInteractivePhase = function () {
        this.tableau.submit();
    };
    Connector.prototype.validateApiToken = function (token) {
        var apiClient = new Client(this.fetch, token, { baseUrl: BASE_API_URL, useCaseHeader: USE_CASE_HEADER });
        return apiClient.fetchCustomerId().catch(function (error) {
            // Only treat 401/403 as invalid API key
            if (error && error.status && (error.status === 401 || error.status === 403)) {
                throw new Error('Invalid API Key');
            }
            // For all other errors (network, server issues, etc.), throw a generic error
            throw new Error('Connection Error');
        });
    };
    Connector.prototype.setApiToken = function (token) {
        var connectionData = this.getConnectionData();
        connectionData.apiToken = token;
        this.saveConnectionData(connectionData);
        if (token !== null) {
            this.apiClient = new Client(this.fetch, token, { baseUrl: BASE_API_URL, useCaseHeader: USE_CASE_HEADER });
        }
    };
    Connector.prototype.isConnectorInitialized = function () {
        return this.connectorInitialized;
    };
    Connector.prototype.getConnectionData = function () {
        try {
            this.isConnectorInitialized();
        }
        catch (_a) {
            throw new Error('Connection Data has not been initialized');
        }
        return ConnectionData.fromJson(this.tableau.connectionData);
    };
    Connector.prototype.saveConnectionData = function (connectionData) {
        this.tableau.connectionData = connectionData.toJson();
    };
    Connector.prototype.getApiClient = function () {
        if (this.apiClient !== undefined) {
            return this.apiClient;
        }
        throw new Error('api client not initialized; set the api token');
    };
    Connector.prototype.getTableau = function () {
        return this.tableau;
    };
    Connector.prototype.getCustomerProfiles = function () {
        var _this = this;
        if (this.customerProfiles === undefined) {
            return this.getApiClient()
                .fetchCustomerProfiles()
                .then(function (r) { return r.json(); })
                .then(function (r) {
                _this.customerProfiles = r.data;
                return _this.customerProfiles;
            });
        }
        else {
            return Promise.resolve(this.customerProfiles);
        }
    };
    Connector.mapCustomerProfilesToIds = function (profiles) {
        return profiles.map(function (cp) { return cp.customer_profile_id; });
    };
    Connector.prototype.getCustomerProfileIds = function () {
        return this.getCustomerProfiles().then(Connector.mapCustomerProfilesToIds);
    };
    Connector.prototype.getCustomerProfileIdsSupportedByAnalytics = function () {
        return this.getCustomerProfiles()
            .then(function (arr) { return arr.filter(function (cp) { return ANALYTICS_SUPPORTED_CUSTOMER_PROFILE_NETWORK_TYPES.includes(cp.network_type); }); })
            .then(Connector.mapCustomerProfilesToIds);
    };
    // Filter out networks (profile types) that are not supported by the /analytics/profiles endpoint
    Connector.prototype.getCustomerProfileIdsSupportedByAnalyticsForProfilesEndpoint = function () {
        var supportedNetworkTypes = ANALYTICS_SUPPORTED_CUSTOMER_PROFILE_NETWORK_TYPES.filter(function (type) { return type !== 'bluesky' && type !== 'snapchat'; });
        return this.getCustomerProfiles()
            .then(function (arr) { return arr.filter(function (cp) { return supportedNetworkTypes.includes(cp.network_type); }); })
            .then(Connector.mapCustomerProfilesToIds);
    };
    Connector.prototype.setDateOptionsKey = function (dateOptionKey) {
        if (dateOptionKey !== null) {
            var connectionData = this.getConnectionData();
            connectionData.dateOptionKey = dateOptionKey;
            this.saveConnectionData(connectionData);
            var startDate = dateFromOptionKey(dateOptionKey);
            this.setStartDate(startDate);
        }
    };
    Connector.prototype.setStartDate = function (startDate) {
        this.startDate = startDate;
    };
    Connector.prototype.getStartDate = function () {
        return this.startDate;
    };
    Connector.prototype.setEndDate = function (endDate) {
        this.endDate = endDate;
    };
    Connector.prototype.getEndDate = function () {
        return this.endDate;
    };
    /**
     * Register all of our Tables and TableauStandardConnections here
     */
    Connector.prototype.setUpSchema = function () {
        this.registerTable(new Profiles());
        this.registerTable(new Posts());
        this.registerTable(new ProfileMetrics());
        this.registerTable(new Groups());
        this.registerTable(new ProfilesToGroups());
        this.registerTable(new Tags());
        this.registerTable(new PostsToTags());
        this.registerStandardConnection(PostPerformance);
        this.registerStandardConnection(ProfilePerformance);
        this.registerStandardConnection(TagPerformance);
    };
    Connector.prototype.registerTable = function (table) {
        if (this.tables.has(table.id)) {
            throw Error("Duplicate Table.id registered: ".concat(table.id));
        }
        this.tables.set(table.id, table);
    };
    Connector.prototype.registerStandardConnection = function (standardConnection) {
        this.standardConnections.push(standardConnection);
    };
    Connector.prototype.tableau_init = function (callback) {
        var connectionData = this.getConnectionData();
        this.setApiToken(connectionData.apiToken);
        this.setDateOptionsKey(connectionData.dateOptionKey);
        this.connectorInitialized = true;
        if (this.connectorInitCallback) {
            this.connectorInitCallback();
        }
        callback();
    };
    Connector.prototype.tableau_getSchema = function (callback) {
        callback(Array.from(this.tables.values()).map(function (t) { return t.getTableInfo(); }), this.standardConnections);
    };
    Connector.prototype.tableau_getData = function (tableauTable, doneCallback) {
        if (this.apiClient === undefined) {
            this.tableau.abortWithError('Internal error: apiClient not initialized');
            throw new Error('apiClient not initialized');
        }
        var table = this.tables.get(tableauTable.tableInfo.id);
        if (table === undefined) {
            this.tableau.abortWithError("Internal error: Unknown table with id ".concat(tableauTable.tableInfo.id));
            throw new Error("Unknown table with id ".concat(tableauTable.tableInfo.id));
        }
        table.getData(this, tableauTable, doneCallback);
    };
    return Connector;
}());
export default Connector;
